import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from "@mui/material";
import { getConceptos, fmtFecha } from "../services/egresosService";
import {
  buscarPresupuestos, egresosDeMacrocuentaMes, ejecutadoAnualMacrocuenta, fmt, MESES, num, resumenPresupuestoMacrocuenta,
} from "../services/presupuestoService";

const cellSx = { py: 0.2, px: 0.6, fontSize: 11 };
const colorDif = (v) => (v > 0 ? "success.main" : v < 0 ? "error.main" : "text.secondary");
const Boton = ({ children, onClick }) => (
  <Box component="a" href="#detalle" onClick={(e) => { e.preventDefault(); onClick(); }} sx={{ color: "primary.main", textDecoration: "underline", cursor: "pointer" }}>{children}</Box>
);

// Comparación por concepto de una macrocuenta en un mes (presupuestado vs ejecutado) con el detalle de cada lado.
function ConceptoDialog({ datos, anio, onClose }) {
  const [pres, setPres] = useState(null);
  const [ejec, setEjec] = useState(null);
  const [error, setError] = useState("");
  const [detalle, setDetalle] = useState(null); // { concepto, lado }

  useEffect(() => {
    if (!datos) return;
    setPres(null); setEjec(null); setError(""); setDetalle(null);
    Promise.all([buscarPresupuestos(anio, { id_macrocuenta: datos.id, limit: 500 }), egresosDeMacrocuentaMes(datos.id, anio, datos.mesNum)])
      .then(([p, e]) => { setPres(p); setEjec(e); })
      .catch(() => setError("No se pudo cargar la comparación."));
  }, [datos, anio]);

  const filas = useMemo(() => {
    if (!pres || !ejec) return [];
    const mapa = new Map();
    const get = (c) => { if (!mapa.has(c)) mapa.set(c, { pres: 0, ejec: 0 }); return mapa.get(c); };
    pres.forEach((r) => { get(String(r.concepto || "").trim() || "(Sin concepto)").pres += num(r[datos.mes]); });
    ejec.forEach((r) => { get(String(r.concepto || "").trim() || "(Sin concepto)").ejec += num(r.bolivianos); });
    return [...mapa.entries()].map(([concepto, v]) => ({ concepto, ...v, dif: v.pres - v.ejec })).sort((a, b) => a.concepto.localeCompare(b.concepto, "es", { sensitivity: "base" }));
  }, [pres, ejec, datos]);

  const tPres = filas.reduce((a, f) => a + f.pres, 0);
  const tEjec = filas.reduce((a, f) => a + f.ejec, 0);
  const detPres = detalle?.lado === "pres" ? (pres || []).filter((r) => (String(r.concepto || "").trim() || "(Sin concepto)") === detalle.concepto) : [];
  const detEjec = detalle?.lado === "ejec" ? (ejec || []).filter((r) => (String(r.concepto || "").trim() || "(Sin concepto)") === detalle.concepto) : [];

  return (
    <Dialog open={!!datos} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
        Comparación por concepto — {datos?.mes} {anio} ({datos?.nombre})
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
        {!error && (!pres || !ejec) && <CircularProgress size={18} />}
        {pres && ejec && (
          <Table size="small" sx={{ "& .MuiTableCell-root": cellSx }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Concepto</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Presupuestado</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Ejecutado (Bs)</TableCell>
                <TableCell sx={{ fontWeight: 800 }} align="right">Diferencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!filas.length ? <TableRow><TableCell colSpan={4}>Sin datos para comparar en {datos?.mes}.</TableCell></TableRow> : filas.map((f) => (
                <TableRow key={f.concepto} hover>
                  <TableCell>{f.concepto}</TableCell>
                  <TableCell align="right"><Boton onClick={() => setDetalle({ concepto: f.concepto, lado: "pres" })}>{fmt(f.pres)}</Boton></TableCell>
                  <TableCell align="right"><Boton onClick={() => setDetalle({ concepto: f.concepto, lado: "ejec" })}>{fmt(f.ejec)}</Boton></TableCell>
                  <TableCell align="right" sx={{ color: colorDif(f.dif), fontWeight: 700 }}>{fmt(f.dif)}</TableCell>
                </TableRow>
              ))}
              {filas.length > 0 && (
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(tPres)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(tEjec)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: colorDif(tPres - tEjec) }}>{fmt(tPres - tEjec)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {detalle && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, mb: 0.5 }}>
              Detalle — {detalle.concepto} — {detalle.lado === "pres" ? "Presupuestado" : "Ejecutado"} — {datos.mes}
            </Typography>
            <TableContainer sx={{ maxHeight: "34vh" }}>
              {detalle.lado === "pres" ? (
                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                  <TableHead><TableRow>{["Nombre", "Cuenta", "Contrato", "Moneda", `Monto ${datos.mes}`, "Observación"].map((h) => <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {detPres.map((r) => (
                      <TableRow key={r.id}><TableCell>{r.nombre}</TableCell><TableCell>{r.cuenta}</TableCell><TableCell>{r.nro_contrato}</TableCell><TableCell>{r.moneda}</TableCell>
                        <TableCell align="right">{fmt(r[datos.mes])}</TableCell><TableCell>{r.Observacion ?? r.observacion}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
                  <TableHead><TableRow>{["ID", "Fecha", "Nombre", "Concepto", "Cuenta", "Ciudad", "Banco", "Ref", "Moneda", "Bs", "$us", "TC", "Obs"].map((h) => <TableCell key={h} sx={{ fontWeight: 800 }}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {detEjec.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.id}</TableCell><TableCell sx={{ whiteSpace: "nowrap" }}>{fmtFecha(r.fecha)}</TableCell><TableCell>{r.nombre}</TableCell><TableCell>{r.concepto}</TableCell>
                        <TableCell>{r.cuenta}</TableCell><TableCell>{r.ciudad}</TableCell><TableCell>{r.banco}</TableCell><TableCell>{r.nocheque}</TableCell><TableCell>{r.moneda}</TableCell>
                        <TableCell align="right">{fmt(r.bolivianos)}</TableCell><TableCell align="right">{fmt(r.dolares)}</TableCell><TableCell align="right">{r.cambio}</TableCell><TableCell>{r.obs}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 0.75 }}><Button size="small" onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

// Comparación Presupuesto vs Ejecutado por macrocuenta y mes (espejo de presupuestos_comparacion.php).
export default function PresupuestoComparacion({ anio }) {
  const [pres, setPres] = useState([]);
  const [ejec, setEjec] = useState([]);
  const [macros, setMacros] = useState(new Map());
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    setCargando(true); setError("");
    Promise.all([resumenPresupuestoMacrocuenta(anio), ejecutadoAnualMacrocuenta(anio), getConceptos()])
      .then(([p, e, c]) => {
        setPres(p); setEjec(e);
        setMacros(new Map((c.data?.macrocuentas || []).map((m) => [Number(m.id), m.nombre])));
      })
      .catch(() => setError("No se pudo cargar la comparación."))
      .finally(() => setCargando(false));
  }, [anio]);

  const filas = useMemo(() => {
    const idxP = new Map(pres.map((r) => [Number(r.id_macrocuenta), r]));
    const idxE = new Map(ejec.map((r) => [Number(r.id_macrocuenta), r]));
    const ids = [...new Set([...idxP.keys(), ...idxE.keys()])].sort((a, b) => a - b);
    return ids.map((id) => {
      const p = idxP.get(id);
      const nombre = p?.macrocuenta || macros.get(id) || `Macrocuenta #${id}`;
      return { id, nombre, p: MESES.map((m) => num(p?.[m])), e: MESES.map((m) => num(idxE.get(id)?.[m])) };
    });
  }, [pres, ejec, macros]);

  const totP = MESES.map((_, i) => filas.reduce((a, f) => a + f.p[i], 0));
  const totE = MESES.map((_, i) => filas.reduce((a, f) => a + f.e[i], 0));
  const suma = (a) => a.reduce((x, y) => x + y, 0);
  const abrir = (f, i) => setSel({ id: f.id, nombre: f.nombre, mes: MESES[i], mesNum: i + 1 });

  if (cargando) return <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!filas.length) return <Alert severity="info">No hay datos de presupuesto ni de egresos para {anio}.</Alert>;

  const filaDatos = (etq, valores, extra) => (
    <>
      <TableCell sx={{ fontWeight: 700 }}>{etq}</TableCell>
      {valores.map((v, i) => <TableCell key={i} align="right" sx={extra ? { color: colorDif(v), fontWeight: 700 } : undefined}>{fmt(v)}</TableCell>)}
      <TableCell align="right" sx={{ fontWeight: 800, ...(extra ? { color: colorDif(suma(valores)) } : {}) }}>{fmt(suma(valores))}</TableCell>
    </>
  );

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 1, py: 0.5, bgcolor: "action.hover" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800 }}>Comparación Presupuesto vs Ejecutado — {anio}</Typography>
      </Box>
      <TableContainer sx={{ maxHeight: "68vh" }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": cellSx }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Macrocuenta</TableCell><TableCell sx={{ fontWeight: 800 }} />
              {MESES.map((m) => <TableCell key={m} align="right" sx={{ fontWeight: 800 }}>{m}</TableCell>)}
              <TableCell align="right" sx={{ fontWeight: 800 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.map((f) => {
              const d = f.p.map((v, i) => v - f.e[i]);
              return (
                <React.Fragment key={f.id}>
                  <TableRow sx={{ borderTop: 2, borderColor: "divider", bgcolor: "action.hover" }}>
                    <TableCell rowSpan={3} sx={{ fontWeight: 800, verticalAlign: "top" }}>{f.nombre}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Presupuesto</TableCell>
                    {f.p.map((v, i) => <TableCell key={i} align="right"><Boton onClick={() => abrir(f, i)}>{fmt(v)}</Boton></TableCell>)}
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(suma(f.p))}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: "info.main" }}>Ejecutado</TableCell>
                    {f.e.map((v, i) => <TableCell key={i} align="right"><Boton onClick={() => abrir(f, i)}>{fmt(v)}</Boton></TableCell>)}
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{fmt(suma(f.e))}</TableCell>
                  </TableRow>
                  <TableRow>{filaDatos("Diferencias", d, true)}</TableRow>
                </React.Fragment>
              );
            })}
            <TableRow sx={{ borderTop: 2, borderColor: "divider", bgcolor: "action.selected" }}>
              <TableCell rowSpan={3} sx={{ fontWeight: 900 }}>TOTAL</TableCell>
              {filaDatos("Presupuesto", totP)}
            </TableRow>
            <TableRow sx={{ bgcolor: "action.selected" }}>{filaDatos("Ejecutado", totE)}</TableRow>
            <TableRow sx={{ bgcolor: "action.selected" }}>{filaDatos("Diferencias", totP.map((v, i) => v - totE[i]), true)}</TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography sx={{ fontSize: 11, p: 1 }} color="text.secondary">Diferencia = presupuesto − ejecutado (verde: queda saldo; rojo: se excedió). Haz clic en un monto para ver la comparación por concepto.</Typography>
      <ConceptoDialog datos={sel} anio={anio} onClose={() => setSel(null)} />
    </Paper>
  );
}
